import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelection,
  type EntityItemConfig,
  type EntityPoolConfig,
  type IconName,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "filter-age-status";

const AGE_STATUS_ITEMS: EntityItemConfig[] = [
  { id: "null", title: "Disabled", keywords: ["disabled", "none"] },
  { id: "onTrack", title: "On Track", keywords: ["on track", "green"] },
  { id: "atRisk", title: "At Risk", keywords: ["at risk", "orange"] },
  { id: "offTrack", title: "Off Track", keywords: ["off track", "red"] },
];

type AgeStatusSelectProps = Omittable<{
  value: unknown;
  onChange: (value: unknown) => void;
  icon?: IconName;
}>;

/**
 * EntitySelector-based age status picker for advanced filters.
 * Renders as a dropdown with multi-select.
 */
export function AgeStatusSelect({ value, onChange, icon }: AgeStatusSelectProps) {
  const pool = useMemo<EntityPoolConfig>(
    () => ({ id: POOL_ID, label: "Age Status", items: AGE_STATUS_ITEMS }),
    [],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 0,
      searchPlaceholder: "Search statuses...",
      emptyMessage: "No statuses found",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    // Narrowed inside the memo: a `[]` fallback built outside it is a new
    // array every render, and the memo would never hold.
    const selectedValues = Array.isArray(value)
      ? (value as (string | null)[])
      : [];
    return selectedValues
      .map((v) => {
        const id = v === null ? "null" : v;
        const entity = AGE_STATUS_ITEMS.find((item) => item.id === id);
        if (!entity) return null;
        return { id, poolId: POOL_ID, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value]);

  const handleChange = useCallback(
    (next: EntitySelection[]) => {
      const values = next.map((s) => (s.id === "null" ? null : s.id));
      onChange(values.length > 0 ? values : null);
    },
    [onChange],
  );

  return (
    <EntitySelector config={config} value={selections} onValueChange={handleChange}>
      {/* The trigger's size scale bottoms out at "sm". */}
      <EntitySelectorTrigger placeholder="Select status..." size="sm" chips icon={icon} />
      <EntitySelectorContent fit>
        <EntitySelectorInput />
        <EntitySelectorPool value={POOL_ID} />
        <EntitySelectorEmpty>No statuses found</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}
